<script setup lang="ts">
import toastThrowError from '@/helpers/toastThrowError'
import type { AssemblyVariables, AssemblyVariableValue } from '@/types/customTypes'
import { computed, ref, toRaw } from 'vue'
import BaseButton from './BaseButton.vue'

type VariableValues = Record<string, AssemblyVariableValue>
const props = defineProps<{ variables?: AssemblyVariables }>()
const variableValues = defineModel<VariableValues>('variableValues', { default: {} })
const emit = defineEmits<{ save: [variableValues: VariableValues] }>()

const localVariableValues = ref<VariableValues>({})

const invalidFields = computed(() => {
  const invalidFields: string[] = []
  const invalidValues = [undefined, null, '']
  if (props.variables) {
    Object.entries(props.variables).forEach((entry) => {
      const variableName = entry[0]
      const variableOptions = entry[1]
      let value = localVariableValues.value[variableName]
      if (variableOptions.type === 'boolean' && value === undefined) {
        // Boolean variables should be treated as false if not set
        localVariableValues.value[variableName] = false
        value = false
      }
      if (invalidValues.includes(value as string)) {
        invalidFields.push(variableName)
      }
    })
  }
  return invalidFields
})

/**Validate that all variables have values */
function saveVariables() {
  if (invalidFields.value.length !== 0) {
    throw toastThrowError(`Mising values for fields: ${invalidFields.value.toString()}`)
  }
  variableValues.value = structuredClone(toRaw(localVariableValues.value))
  emit('save', structuredClone(toRaw(localVariableValues.value)))
}
</script>
<template>
  <div class="flex flex-col gap-3 rounded-lg bg-gray-100 p-3">
    <h3 class="w-full text-center text-xl font-bold">Assembly Variables</h3>
    <table class="h-full table-auto overflow-auto rounded-lg p-3">
      <thead>
        <tr class="text-center">
          <th class="p-1">Name</th>
          <th class="p-1">Unit</th>
          <th class="p-1">Value</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(assemblyVariableOption, assemblyVariableName) in props.variables"
          :key="assemblyVariableName"
        >
          <td class="p-1 text-center italic">{{ assemblyVariableName }}</td>
          <td class="p-1 text-center">{{ assemblyVariableOption.unit_of_measure }}</td>
          <td class="p-1 text-center">
            <input
              v-if="assemblyVariableOption.type === 'number'"
              v-model.number="localVariableValues[assemblyVariableName]"
              class="rounded-lg border border-gray-300 bg-gray-50 p-1 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
              type="text"
            />
            <input
              v-else-if="assemblyVariableOption.type === 'boolean'"
              :value="localVariableValues[assemblyVariableName]"
              @change="
                localVariableValues[assemblyVariableName] =
                  !localVariableValues[assemblyVariableName]
              "
              class="h-4 w-4 rounded-sm border-gray-300 bg-gray-100 text-blue-600"
              type="checkbox"
            />
            <select
              v-else-if="assemblyVariableOption.type === 'select'"
              v-model="localVariableValues[assemblyVariableName]"
              class="w-full rounded-lg border border-gray-300 bg-gray-50 p-1 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
            >
              <option
                v-for="option in assemblyVariableOption.selectOptions"
                :key="option"
                :value="option"
              >
                {{ option }}
              </option>
            </select>
          </td>
        </tr>
      </tbody>
    </table>
    <BaseButton :disabled="invalidFields.length !== 0" @click="saveVariables">Save</BaseButton>
  </div>
</template>
